import apiClient from './base/apiClient'
import {
  RegisterRequest,
  RegisterResponse,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  EmailCheckRequest,
  EmailCheckResponse,
  UsernameCheckRequest,
  UsernameCheckResponse,
  PhoneCheckRequest,
  PhoneCheckResponse,
  ApiErrorResponse,
  VerifySecurityAnswerForPasswordFindRequest,
  VerifySecurityAnswerForPasswordFindResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  SocialLoginRequest,
  SocialLoginResponse,
  VerifyPhoneRequest,
  VerifyPhoneResponse,
  SendOtpRequest,
  SendOtpResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
  LoginOtpRequest,
  LoginOtpResponse,
  LinkSocialOtpRequest,
} from './auth.types'

export const authApi = {
  register: async (data: RegisterRequest): Promise<RegisterResponse> => {
    const formData = new FormData()

    if (data.email) {
      formData.append('email', data.email)
    }

    if (data.phone) {
      formData.append('phone', data.phone)
    }
    formData.append('username', data.username)
    if (data.password) {
      formData.append('password', data.password)
    }
    formData.append('nickname', data.nickname)
    formData.append('agree_terms', data.agree_terms ? '1' : '0')
    formData.append('agree_privacy', data.agree_privacy ? '1' : '0')
    // 선택 항목. 체크하지 않았으면 0으로 보내 '동의하지 않음'을 명시한다
    formData.append('agree_marketing', data.agree_marketing ? '1' : '0')
    if (data.referral_code) {
      formData.append('referral_code', data.referral_code)
    }

    if (data.profile_image) {
      formData.append('profile_image', data.profile_image)
    }

    if (data.provider && data.provider_id) {
      formData.append('provider', data.provider)
      formData.append('provider_id', data.provider_id)
    }

    const response = await apiClient.post<RegisterResponse>(
      '/auth/register',
      formData,
      { skipErrorHandler: true } as any
    )

    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token)
    }

    return response.data
  },

  checkEmail: async (data: EmailCheckRequest): Promise<EmailCheckResponse> => {
    const response = await apiClient.post<EmailCheckResponse>(
      '/auth/check-email',
      data,
      { skipErrorHandler: true } as any
    )
    return response.data
  },

  checkUsername: async (
    data: UsernameCheckRequest
  ): Promise<UsernameCheckResponse> => {
    const response = await apiClient.post<UsernameCheckResponse>(
      '/auth/check-username',
      data,
      { skipErrorHandler: true } as any
    )
    return response.data
  },

  checkPhone: async (
    data: PhoneCheckRequest
  ): Promise<PhoneCheckResponse> => {
    const response = await apiClient.post<PhoneCheckResponse>(
      '/auth/check-phone',
      data
    )
    return response.data
  },

  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>(
      '/auth/login',
      data,
      { skipErrorHandler: true } as any
    )

    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token)
    }

    return response.data
  },

  logout: async (): Promise<LogoutResponse> => {
    const response = await apiClient.post<LogoutResponse>('/auth/logout')
    localStorage.removeItem('auth_token')
    return response.data
  },

  verifySecurityAnswerForPasswordFind: async (
    data: VerifySecurityAnswerForPasswordFindRequest
  ): Promise<VerifySecurityAnswerForPasswordFindResponse> => {
    const response =
      await apiClient.post<VerifySecurityAnswerForPasswordFindResponse>(
        '/auth/find-password/verify',
        data
      )
    return response.data
  },

  resetPassword: async (
    data: ResetPasswordRequest
  ): Promise<ResetPasswordResponse> => {
    const response = await apiClient.post<ResetPasswordResponse>(
      '/auth/find-password/reset',
      data,
      { skipErrorHandler: true } as any
    )
    return response.data
  },

  socialLogin: async (
    data: SocialLoginRequest
  ): Promise<SocialLoginResponse> => {
    const response = await apiClient.post<SocialLoginResponse>(
      '/auth/social-login',
      data,
      { skipErrorHandler: true } as any
    )

    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token)
    }

    return response.data
  },

  naverCallback: async (
    code: string,
    state: string
  ): Promise<{ provider_id: string }> => {
    const response = await apiClient.post<{ provider_id: string }>(
      '/auth/naver-callback',
      { code, state },
      {
        skipErrorHandler: true,
      } as any
    )
    return response.data
  },

  verifyPhone: async (
    data: VerifyPhoneRequest
  ): Promise<VerifyPhoneResponse> => {
    const response = await apiClient.post<VerifyPhoneResponse>(
      '/auth/verify-phone',
      data
    )
    return response.data
  },

  // 솔라피 SMS 인증번호 발송
  sendOtp: async (data: SendOtpRequest): Promise<SendOtpResponse> => {
    const response = await apiClient.post<SendOtpResponse>(
      '/auth/send-otp',
      data,
      { skipErrorHandler: true } as any
    )
    return response.data
  },

  // 솔라피 SMS 인증번호 검증
  verifyOtp: async (data: VerifyOtpRequest): Promise<VerifyOtpResponse> => {
    const response = await apiClient.post<VerifyOtpResponse>(
      '/auth/verify-otp',
      data,
      { skipErrorHandler: true } as any
    )
    return response.data
  },

  // 기존 계정 OTP 로그인 (verify-otp 통과 후 토큰 발급)
  loginOtp: async (data: LoginOtpRequest): Promise<LoginOtpResponse> => {
    const response = await apiClient.post<LoginOtpResponse>(
      '/auth/login-otp',
      data,
      { skipErrorHandler: true } as any
    )

    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token)
    }

    return response.data
  },

  // 소셜 + 기존번호 계정 연결 후 로그인 (verify-otp 통과 후)
  linkSocialOtp: async (
    data: LinkSocialOtpRequest
  ): Promise<LoginOtpResponse> => {
    const response = await apiClient.post<LoginOtpResponse>(
      '/auth/link-social-otp',
      data,
      { skipErrorHandler: true } as any
    )

    if (response.data.token) {
      localStorage.setItem('auth_token', response.data.token)
    }

    return response.data
  },
}

export type {
  RegisterRequest,
  RegisterResponse,
  LoginRequest,
  LoginResponse,
  LogoutResponse,
  EmailCheckRequest,
  EmailCheckResponse,
  UsernameCheckRequest,
  UsernameCheckResponse,
  PhoneCheckRequest,
  PhoneCheckResponse,
  ApiErrorResponse,
  VerifySecurityAnswerForPasswordFindRequest,
  VerifySecurityAnswerForPasswordFindResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  SocialLoginRequest,
  SocialLoginResponse,
  VerifyPhoneRequest,
  VerifyPhoneResponse,
  SendOtpRequest,
  SendOtpResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
  LoginOtpRequest,
  LoginOtpResponse,
}
